<template>
<div>
<v-app-bar app dark flat clipped-left clipped-right class="menu">
<v-container class="px-4 py-0 fill-height flex-nowrap">
    <router-link :to="{name: 'account'}" class="mr-4">
        <img src="/static/logo.svg" height="38" style="vertical-align: middle;" alt="coroot logo">
    </router-link>
    <v-spacer />
    <v-menu v-if="account" offset-y tile attach=".v-app-bar" left>
        <template #activator="{ on }">
            <v-btn v-on="on" outlined plain height="40" class="px-2">
                <v-icon>mdi-account</v-icon>
            </v-btn>
        </template>
        <v-list dense dark>
            <v-list-item class="d-block py-3">
                <div v-if="account.FirstName || account.LastName">
                    Hi, {{account.FirstName}} {{account.LastName}}
                </div>
                <div class="grey--text">
                    {{account.Email}}
                </div>
            </v-list-item>
            <v-divider />
            <v-list-item :to="{name: 'logout'}">
                <v-icon small class="mr-1">mdi-logout-variant</v-icon>
                Log out
            </v-list-item>
        </v-list>
    </v-menu>
</v-container>
</v-app-bar>

<v-main style="padding-bottom: 100px">
<v-container class="px-4 py-0">
    <template v-if="anonymous">
        <h1>Welcome to Coroot Customer Portal</h1>
        <v-row>
            <v-col cols="12" sm="6">
                <v-row v-for="v in features" class="feature">
                    <v-col class="flex-grow-0">
                        <GreenArrow class="mt-1" />
                    </v-col>
                    <v-col class="flex-grow-1">
                        <p class="header">{{v.header}}</p>
                        <p class="message">{{v.body}}</p>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" sm="6">
                <SigninForm v-if="page === 'signin'" />
                <SignupForm v-if="page === 'signup'" />
            </v-col>
        </v-row>
    </template>

    <template v-else-if="account">
        <SetPassword v-if="!account.PasswordSet" />
        <AccountActivation v-else-if="!account.Activated" :email="account.Email" />
        <template v-else>
            <h2>Organization</h2>
            <Organization :id="organization.id" ref="organization" />

            <h2>Coroot Enterprise licenses</h2>
            <Licenses :organizationId="organization.id" @refresh="() => $refs.organization.get()" />

            <h2>API Keys</h2>
            <OrgApiKeys :organizationId="organization.id" />
        </template>
    </template>

</v-container>
</v-main>

<v-footer app absolute inset padless class="text-center">
    <v-container class="pa-2 footer">
        <div class="d-flex flex-wrap align-center justify-center" style="column-gap: 40px">
            <div class="copy">&copy; {{ new Date().getFullYear() }} Coroot Inc., All Rights Reserved.</div>
        </div>
    </v-container>
</v-footer>

</div>
</template>

<script>
import Intercom from '@intercom/messenger-js-sdk';
import AccountActivation from "@/pages/auth/AccountActivation.vue";
import SignupForm from "@/components/SignupForm.vue";
import SigninForm from "@/components/SigninForm.vue";
import GreenArrow from "@/components/GreenArrow.vue";
import SetPassword from "@/pages/auth/SetPassword.vue";
import Organization from "@/pages/Organization.vue";
import Licenses from "@/pages/Licenses.vue";
import OrgApiKeys from "@/pages/OrgApiKeys.vue";

export default {
    props: {
        page: String,
    },

    components: {GreenArrow, SignupForm, SigninForm, SetPassword, AccountActivation, Organization, Licenses, OrgApiKeys},

    data() {
        return {
            anonymous: false,
            account: null,
            intercom: null,
        };
    },

    mounted() {
        if (this.$route.query.google) {
            this.anonymous = true;
            return;
        }
        this.get();
    },

    beforeDestroy() {
        this.intercom && this.intercom('shutdown');
    },

    watch: {
        $route() {
            this.get();
        }
    },

    computed: {
        features() {
            return [
                {
                    header: 'Get your free Coroot Enterprise trial',
                    body: "Get started with Coroot Enterprise instantly, no credit card or sales calls required.",
                },
                {
                    header: 'Activate your Coroot Enterprise subscription now',
                    body: "Coroot Enterprise works for companies of any size, from SMB to large enterprises. If you don’t need customized terms, you can activate a subscription and pay online without talking to anyone.",
                },
                {
                    header: 'Manage your Coroot Enterprise licenses easily',
                    body: "Manage your subscriptions, payment methods, and payments history.",
                },
            ]
        },
        organization() {
            return this.account ? this.account.Organizations[0] : {id: '', name: ''};
        },
    },

    methods: {
        get() {
            this.$api.checkToken((anonymous) => {
                this.anonymous = anonymous;
                if (anonymous) {
                    if (!this.page) {
                        this.$router.replace({name: 'signup'}).catch(err => err);
                    }
                    return;
                }
                this.$api.account((data) => {
                    this.account = data;
                    if (window.Coroot.IntercomAppId) {
                        this.intercom = Intercom({
                            app_id: window.Coroot.IntercomAppId,
                            intercom_user_jwt: this.account.intercom_user_jwt,
                            company: {
                                company_id: this.organization.id,
                                name: this.organization.name,
                            },
                        });
                    }
                });
            });
        },
    },
}
</script>

<style>
* {
    font-family: Poppins, sans-serif;
}
.font-size-small {
    font-size: .875rem;
}
.font-size-x-small {
    font-size: .75rem;
}
</style>

<style scoped>
.menu {
    background-color: #080b1d !important;
}
.menu:deep(.v-btn) {
    min-width: unset !important;
    border-color: rgba(255,255,255,0.2);
}
.menu:deep(.v-btn):hover {
    border-color: rgba(255,255,255,1);
}

.feature .header {
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    margin-bottom: 12px;
}
.feature .message {
    font-size: 16px;
}

.footer {
    border-top: thin solid var(--border-color);
    justify-content: center;
    font-size: 14px;
    line-height: 2;
}
.footer .copy {
    color: var(--text-color-dimmed);
}

</style>
